import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MessageCircle, Mail, MessageSquare, Link, Twitter, Share2, Instagram } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOverlayState } from '../context/OverlayContext';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  shareUrl: string;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, title, shareUrl }) => {
  useOverlayState(isOpen, 'share-modal');
  const [copied, setCopied] = React.useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const shareText = `Check out this Vybe: ${title}`;
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-[#25D366] hover:bg-[#1fb855]',
      action: () => {
        window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, '_blank');
      },
    },
    {
      name: 'Instagram',
      icon: Instagram,
      color: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
      action: () => {
        handleCopyLink();
        window.open('https://www.instagram.com/', '_blank');
      },
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'bg-[#0078D4] hover:bg-[#0063b1]',
      action: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodedText}%20${encodedUrl}`;
      },
    },
    {
      name: 'SMS',
      icon: MessageSquare,
      color: 'bg-[#8B5CF6] hover:bg-[#7C3AED]',
      action: () => {
        window.location.href = `sms:?body=${encodedText}%20${encodedUrl}`;
      },
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'bg-[#1DA1F2] hover:bg-[#1a8cd8]',
      action: () => {
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank');
      },
    },
    {
      name: copied ? 'Copied!' : 'Copy Link',
      icon: Link,
      color: 'bg-white/10 hover:bg-white/20 border border-white/20',
      action: handleCopyLink,
    },
  ];

  const portalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          <div
            className="nav-popover-backdrop"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="nav-popover-panel rounded-2xl border border-theme-cards bg-theme-cards shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <Share2 size={20} className="mr-2 text-theme-primary" />
                  <h2 className="text-lg font-semibold text-white">Share Vybe</h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-theme-text-secondary hover:text-white p-1"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Event Title - Compact */}
              <div className="mb-4 pb-3 border-b border-white/10">
                <p className="text-white/80 font-medium line-clamp-2 text-sm">{title}</p>
              </div>

              {/* Share Options - Grid Layout */}
              <div className="grid grid-cols-2 gap-2.5">
                {shareOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <motion.button
                      key={option.name}
                      whileTap={{ scale: 0.95 }}
                      onClick={option.action}
                      className={`flex flex-col items-center gap-2 px-3 py-3.5 rounded-xl ${option.color} transition-all text-white font-medium shadow-sm text-sm`}
                    >
                      <Icon size={20} strokeWidth={2.5} />
                      <span className="text-xs font-semibold">{option.name}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(portalContent, document.body);
};

export default ShareModal;
